import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Compass, Target, Mail } from "lucide-react";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import MarketingPageHero from "@/components/marketing/MarketingPageHero";
import { usePageMeta } from "@/lib/usePageMeta";

const PLACEHOLDER_CARDS = [
  { role: "Founder / CEO" },
  { role: "Head of Engineering" },
  { role: "Head of Product" },
  { role: "Head of Growth" },
];

export default function About() {
  usePageMeta(
    "About — Leadmato",
    "Leadmato is built by Win Massive Impact Pvt. Ltd. to help Indian SMBs find real customers by reading what people actually write online.",
  );
  return (
    <MarketingLayout>
      <MarketingPageHero
        eyebrow="About"
        title={<>Small team.<br />Sharp mission.</>}
        subtitle={<>Leadmato is built by <strong className="text-white">Win Massive Impact Pvt. Ltd.</strong> — a small studio focused on giving Indian founders sharper, faster tools than the global averages give them.</>}
      />

      <section className="py-12 md:py-16 bg-lm-surface border-y border-lm-border">
        <div className="max-w-5xl mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card icon={Compass} title="Our mission" body="Give every Indian SMB owner a way to hear their market — the words, the pain points, the buying signals — without hiring a research team." />
          <Card icon={Target} title="What we believe" body="Real customer intent lives in real conversations. Ads infer it, dashboards summarise it, and most tools ignore it. We read it directly." />
          <Card icon={Building2} title="Where we're built" body="Bengaluru → the world. Made in India, tuned for Indian business categories, priced in ₹, built to work on flaky internet and small budgets." />
        </div>
      </section>

      <section className="py-14 md:py-20 bg-lm-bg">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="text-center">
            <div className="text-[11px] font-mono uppercase tracking-widest text-lm-text3">Team</div>
            <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-lm-text tracking-tight">
              People behind the product
            </h2>
            <p className="mt-3 text-sm md:text-base text-lm-text2 max-w-lg mx-auto">
              We're staying head-down until launch. Team bios coming soon.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {PLACEHOLDER_CARDS.map((p, i) => (
              <div
                key={i}
                data-testid={`about-team-placeholder-${i}`}
                className="rounded-2xl border border-dashed border-lm-border bg-lm-surface p-5 flex flex-col items-center text-center"
              >
                <div className="w-16 h-16 rounded-full bg-lm-tint2 border border-lm-border flex items-center justify-center mb-3">
                  <Building2 className="w-6 h-6 text-lm-primary/60" />
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">{p.role}</div>
                <div className="mt-1 text-sm text-lm-text2">Coming soon</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-lm-surface border-t border-lm-border">
        <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
          <Mail className="w-6 h-6 text-lm-primary mx-auto mb-3" />
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-lm-text">Get in touch</h2>
          <p className="mt-3 text-sm md:text-base text-lm-text2">
            Have a partnership idea, press question, or feedback on the product?
          </p>
          <Link
            to="/contact"
            data-testid="about-contact-cta"
            className="mt-5 inline-flex items-center gap-2 bg-lm-primary hover:bg-lm-primaryHover text-white text-sm font-semibold px-5 py-2.5 rounded-lg"
          >
            Contact us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}

function Card({ icon: Ico, title, body }) {
  return (
    <div className="rounded-2xl bg-lm-bg border border-lm-border p-6">
      <div className="w-9 h-9 rounded-lg bg-lm-tint2 text-lm-primary flex items-center justify-center mb-3">
        <Ico className="w-4 h-4" />
      </div>
      <h3 className="font-display text-lg font-semibold text-lm-text">{title}</h3>
      <p className="mt-2 text-sm text-lm-text2 leading-relaxed">{body}</p>
    </div>
  );
}
